import React, { useEffect, useState } from "react";
import Button from "components/Button";
import { getEmployeeAssignmentsAPI } from "api/apiHelper";
import { toast } from "react-toastify";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  revieweeId: number | null;
  revieweeName: string;
  initialSelectedIds: number[];
  onConfirm: (selectedIds: number[]) => void;
}

const ClientScheduleModal = ({
  isOpen,
  onClose,
  revieweeId,
  revieweeName,
  initialSelectedIds,
  onConfirm,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);

  // Sync state when modal opens
  useEffect(() => {
    if (isOpen && revieweeId !== null) {
      // Clean the incoming IDs just in case the parent has 'undefined'
      const cleanInitial = (initialSelectedIds || []).filter(id => id !== undefined && id !== null);
      setSelectedClientIds(cleanInitial);
      loadModalData(cleanInitial);
    }
  }, [isOpen, revieweeId]);

  const loadModalData = async (currentSelections: number[]) => {
    setLoading(true);
    try {
      const response = await getEmployeeAssignmentsAPI(revieweeId!);
      const data = response.data?.results || response.data || [];
      
      console.log("API Response for employee", revieweeId, ":", response.data);

      const formatted = data.map((asgn: any) => ({
        // Get the correct client ID from the API response
        id: asgn.client_id,
        full_name: asgn.client_name || `Client ID: ${asgn.client_id}`,
      })).filter((c: any) => c.id !== undefined && c.id !== null && c.id !== 0);

      console.log("Formatted clients for modal:", formatted);
      setClients(formatted);

      // Logic: If parent has no saved config yet, use current selections if provided
      if (currentSelections.length === 0) {
        setSelectedClientIds([]);
      } else {
        // Only keep selections that exist in the fetched clients
        const validIds = currentSelections.filter(id => 
          formatted.some((c: any) => c.id === id)
        );
        console.log("Valid selections from parent:", validIds, "Invalid:", currentSelections.filter(id => !formatted.some((c: any) => c.id === id)));
        setSelectedClientIds(validIds);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load client assignments");
    } finally {
      setLoading(false);
    }
  };

  const toggleClient = (id: number) => {
    if (id === undefined || id === null) return;
    setSelectedClientIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSave = () => {
    // Final check: Filter out anything that isn't a valid number
    const finalIds = selectedClientIds.filter(id => typeof id === 'number' || !isNaN(id));
    
    console.log("Modal is sending to parent:", finalIds);
    onConfirm(finalIds);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-navy-800 rounded-xl p-6 w-full max-w-md shadow-2xl">
        <div className="mb-5">
          <h3 className="text-xl font-bold text-navy-700 dark:text-white">
            Client Visibility Control
          </h3>
          <p className="text-sm text-gray-500">Employee: {revieweeName}</p>
        </div>

        {loading ? (
          <div className="py-10 text-center text-navy-700 dark:text-white font-medium">
            <i className="fas fa-spinner fa-spin mr-2"></i> Loading Clients...
          </div>
        ) : (
          <div className="space-y-6">
            <div>
              <label className="text-sm font-bold text-navy-700 dark:text-white mb-2 block">
                Select Clients who should see this appraisal:
              </label>
              <div className="max-h-60 overflow-y-auto border dark:border-navy-700 rounded-lg p-2 space-y-1">
                {clients.length === 0 ? (
                  <p className="text-center py-4 text-xs text-gray-400">
                    No client accounts found.
                  </p>
                ) : (
                  clients.map((client) => (
                    <div
                      key={client.id}
                      className="flex items-center gap-3 p-2 hover:bg-gray-50 dark:hover:bg-navy-700 rounded transition-colors"
                    >
                      <input
                        type="checkbox"
                        id={`client-${client.id}`}
                        className="h-4 w-4 rounded border-gray-300 text-brand-500 cursor-pointer"
                        checked={selectedClientIds.includes(client.id)}
                        onChange={() => toggleClient(client.id)}
                      />
                      <label
                        htmlFor={`client-${client.id}`}
                        className="text-sm text-navy-700 dark:text-white cursor-pointer flex-1"
                      >
                        {client.full_name}
                      </label>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
          <Button varient="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            varient="primary"
            onClick={handleSave}
            disabled={loading}
          >
            Confirm Configuration
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ClientScheduleModal;